
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Lock as LockIcon,
  VerifiedUser as VerifiedUserIcon,
  School as SchoolIcon,
  Language as LanguageIcon,
  Support as SupportIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle,
  Speed,
  MenuBook,
  EmojiEvents,
  Verified,
} from '@mui/icons-material';
import LocalizedLink from './LocalizedLink';
import { SEO } from './SEO';

const SarvalokEnterprise: React.FC = () => {
  const { t } = useTranslation(['enterprise', 'seo']);

  return (
    <div className="bg-white text-black">
      <SEO
        title={t('seo:org.title', { defaultValue: 'Enterprise Solutions | Sarvalok AI for Organizations' })}
        description={t('seo:org.description', { defaultValue: "Transform your organization with authentic cultural intelligence. Leverage Sarvalok's AI platform for research, content creation, and cultural education initiatives." })}
        url="/enterprise"
      />

      {/* Hero Header */}
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-8 lg:px-20 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <BusinessIcon className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            {t('enterprise.header_title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {t('enterprise.header_description')}
          </p>
        </div>
      </div>

      <div className="px-8 lg:px-20 py-16">

        {/* Why Choose Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('enterprise.why_choose_title')}</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <SchoolIcon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('enterprise.accelerate_research_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('enterprise.accelerate_research_desc')}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <SupportIcon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('enterprise.enhance_curricula_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('enterprise.enhance_curricula_desc')}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                  <VerifiedUserIcon className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('enterprise.reliable_knowledge_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('enterprise.reliable_knowledge_desc')}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <LockIcon className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('enterprise.support_disciplines_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('enterprise.support_disciplines_desc')}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                  <LanguageIcon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('enterprise.multilingual_capabilities_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('enterprise.multilingual_capabilities_desc')}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                  <SchoolIcon className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('enterprise.empower_institutes_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('enterprise.empower_institutes_desc')}
              </p>
            </div>
          </div>
        </div>

        {/* Sarvalok Advantage */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-2xl p-12 mb-16 text-white">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <EmojiEvents className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t('enterprise.advantage_title')}</h2>
            <div className="w-24 h-1 bg-white/30 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <Verified className="text-blue-300 mr-3" />
                <h4 className="text-xl font-semibold text-blue-200">{t('enterprise.unmatched_accuracy')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('enterprise.unmatched_accuracy_desc')}
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <MenuBook className="text-green-300 mr-3" />
                <h4 className="text-xl font-semibold text-green-200">{t('enterprise.heritage_ai_insights')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('enterprise.heritage_ai_insights_desc')}
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <Speed className="text-purple-300 mr-3" />
                <h4 className="text-xl font-semibold text-purple-200">{t('enterprise.direct_source_exploration')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('enterprise.direct_source_exploration_desc')}
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <Speed className="text-orange-300 mr-3" />
                <h4 className="text-xl font-semibold text-orange-200">{t('enterprise.efficiency_gains')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('enterprise.efficiency_gains_desc')}
              </p>
            </div>
          </div>
        </div>

        {/* Enlightened Plan */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('enterprise.enlightened_plan_title')}</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('enterprise.enlightened_plan_desc')}
            </p>
          </div>

          <div className="max-w-2xl mx-auto bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8">
            <ul className="space-y-4">
              {[
                t('enterprise.unlimited_tokens'),
                t('enterprise.dedicated_support'),
                t('enterprise.custom_training'),
                t('enterprise.advanced_features'),
                t('enterprise.ad_free_experience'),
                t('enterprise.custom_integration'),
              ].map((item, idx) => (
                <li key={idx} className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 dark:text-gray-200 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Security & Compliance */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-600 p-8 mb-16">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <SecurityIcon className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('enterprise.security_compliance_title')}</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
            {t('enterprise.security_compliance_desc')}{' '}
            <LocalizedLink to="/security" className="text-blue-600 underline hover:text-blue-700 transition-colors">
              {t('enterprise.security_page_link')}
            </LocalizedLink>.
          </p>
        </div>

        {/* Partner With Sarvalok */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-12 mb-16">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('enterprise.partner_title')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
              {t('enterprise.partner_desc')}
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-600 p-12 text-center">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('enterprise.contact_team_title')}</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <a href="mailto:Enterprise@sarvalok.com" className="flex flex-col items-center space-y-2 hover:opacity-80 transition-opacity">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-lg">✉</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('enterprise.email_label')}</p>
              <p className="text-lg font-semibold text-blue-600">{t('enterprise.email_address')}</p>
            </a>

            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-lg">📞</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('enterprise.phone_label')}</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{t('enterprise.phone_number')}</p>
            </div>

            <a href="https://wa.me/918125831103?text=Hello%2C%20I%20am%20inquiring%20about%20the%20Sarvalok%20Enterprise%20plan%20for%20my%20institution.%20Can%20you%20please%20help%20me%3F" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center space-y-2 hover:opacity-80 transition-opacity">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <WhatsAppIcon className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('enterprise.whatsapp_label')}</p>
              <p className="text-lg font-semibold text-green-600">{t('enterprise.whatsapp_text')}</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SarvalokEnterprise;
